import { CommonModule, KeyValuePipe } from '@angular/common';
import { Component, Input, OnInit, ViewChild, effect, inject } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { FormMode } from 'src/app/controls/common/form-mode';
import { AuthenticationType } from 'src/app/models/RestApiCaller/Enums/authentication-type';
import { ContextOfUse } from 'src/app/models/RestApiCaller/Enums/context-of-use';
import { HttpVerb } from 'src/app/models/RestApiCaller/Enums/http-verb';
import { RestApiRequestFormatType } from 'src/app/models/RestApiCaller/Enums/rest-api-request-format-type';
import { RestApiRequestConfiguration } from 'src/app/models/RestApiCaller/configuration/rest-api-request-configuration';
import { ApiConfigurationDetailContext } from '../../api-configuration-detail-context';
import { ContextMenuItemEvent } from '../../common/models/context-menu-item-event';
import { MultiLevelContextMenuComponent } from '../../common/multi-level-context-menu/multi-level-context-menu.component';
import { ApiConfigurationState } from '../models/api-configuration-state';
import { HeadersFormComponent } from './headers-form/headers-form.component';

@Component({
    selector: 'app-api-configuration-request',
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatOptionModule,
        MatInputModule,
        MatSelectModule,
        MatButtonModule,
        KeyValuePipe,
        HeadersFormComponent,
        MultiLevelContextMenuComponent,
    ],
    templateUrl: './api-configuration-request.component.html',
    styleUrl: './api-configuration-request.component.scss',
})
export class ApiConfigurationRequestComponent implements OnInit {
    /**
     * Etat de la configuration d'API
     */
    public readonly apiConfigurationState = inject(ApiConfigurationState);

    /**
     * Liste des enums utilisés dans le template.
     */
    public FormMode = FormMode;
    public ContextOfUse = ContextOfUse;
    public HttpVerb = HttpVerb;

    /**
     * Contexte de la configuration de l'API.
     */
    @Input() context: ApiConfigurationDetailContext;

    /**
     * Formulaire contenant les informations relatives à la requête de la configuration d'API.
     */
    public apiConfigurationRequestForm: FormGroup;

    /**
     * Liste des verbes Http et leur libellé.
     */
    public httpVerbs: Map<HttpVerb, string> = new Map<HttpVerb, string>();

    /**
     * Liste des types d'authentification et leur libellé.
     */
    public authenticationTypes: Map<AuthenticationType, string> = new Map<AuthenticationType, string>();

    /**
     * Liste des types de format de requête et leur libellé.
     */
    public requestFormatTypes: Map<RestApiRequestFormatType, string> = new Map<RestApiRequestFormatType, string>();

    /**
     * Composant du menu contextuel
     */
    @ViewChild(MultiLevelContextMenuComponent)
    contextMenu: MultiLevelContextMenuComponent;

    /**
     * Index du début du texte sélectionné dans le champ (utilisé pour le placement d'une variable avec le menu contextuel).
     */
    currentSelectionStart: number;

    /**
     * Index de fin du texte sélectionné dans le champ sujet (utilisé pour le placement d'une variable avec le menu contextuel).
     */
    currentSelectionEnd: number;

    /**
     * Position horizontale de la sourris au moment d'un clic droit (utilisé pour positionner le menu contextuel)
     */
    rightClickMenuPositionX: number;

    /**
     * Position verticale de la sourris au moment d'un clic droit (utilisé pour positionner le menu contextuel)
     */
    rightClickMenuPositionY: number;

    /**
     * Valeur indiquant si le menu contextuel est visible.
     */
    isContextMenuVisible: boolean;

    /**
     * Constructeur.
     */
    constructor() {
        this.httpVerbs.set(HttpVerb.Get, 'GET');
        this.httpVerbs.set(HttpVerb.Post, 'POST');
        this.httpVerbs.set(HttpVerb.Put, 'PUT');
        this.httpVerbs.set(HttpVerb.Patch, 'PATCH');
        this.httpVerbs.set(HttpVerb.Delete, 'DELETE');

        this.authenticationTypes.set(AuthenticationType.None, 'Aucune');
        this.authenticationTypes.set(AuthenticationType.Basic, 'Authentification classique par identifiant/mot de passe');
        this.authenticationTypes.set(AuthenticationType.Bearer, 'Authentification par jeton Bearer');
        this.authenticationTypes.set(AuthenticationType.ApiKey, "Authentification par clé d'API");

        this.requestFormatTypes.set(RestApiRequestFormatType.Text, 'Texte brut');
        this.requestFormatTypes.set(RestApiRequestFormatType.Json, 'JSON');
        this.requestFormatTypes.set(RestApiRequestFormatType.Xml, 'XML');
        this.requestFormatTypes.set(RestApiRequestFormatType.FormData, 'multipart-form-data');

        effect(() => {
            if (this.apiConfigurationState.$mode() === FormMode.Reading) {
                this.apiConfigurationRequestForm.disable();
            } else {
                this.apiConfigurationRequestForm.enable();
                if (this.apiConfigurationState.$apiConfiguration().contextOfUse === ContextOfUse.AutocompletionDatasource) {
                    this.apiConfigurationRequestForm.get('httpVerb').disable();
                }
            }
        });
    }

    /**
     * Appellé lors de l'initialisation du composant.
     */
    ngOnInit(): void {
        console.log('ApiConfigurationRequestComponent --> ngOnInit');
        if (this.context.apiConfigurationContext.restApiConfiguration.contextOfUse == ContextOfUse.WorkflowAction) {
            this.apiConfigurationRequestForm = new FormGroup({
                httpVerb: new FormControl(''),
                requestFormat: new FormControl(''),
                url: new FormControl(''),
                requestContent: new FormControl(''),
                headers: new UntypedFormControl([]),
                authentication: new FormControl(AuthenticationType.None),
                username: new FormControl(''),
                password: new FormControl(''),
                bearer: new FormControl(''),
                apiKey: new FormControl(''),
            });
        } else {
            this.apiConfigurationRequestForm = new FormGroup({
                httpVerb: new FormControl(),
                requestFormat: new FormControl(''),
                url: new FormControl(''),
                requestContent: new FormControl(''),
                headers: new UntypedFormControl([]),
                authentication: new FormControl(AuthenticationType.None),
                username: new FormControl(''),
                password: new FormControl(''),
                bearer: new FormControl(''),
                apiKey: new FormControl(''),
            });

            this.apiConfigurationRequestForm.get('httpVerb').disable();
        }

        this.fillForm();
    }

    /**
     * Levé lorsque l'utilisateur fait un clic-droit sur un champ de saisie.
     * @param source : Identifiant du composant. Permet de déterminer dans quel composant la variable sera ajouté.
     * @param event : Événement souris.
     */
    public onInputRightClick(source: string, event: MouseEvent) {
        this.isContextMenuVisible = true;

        // Position de la souris utilisé pour placer le menu contextuel.
        this.rightClickMenuPositionX = event.clientX;
        this.rightClickMenuPositionY = event.clientY;

        // Récupère la sélection dans le champ Input. Le paramètre remplacera cette selection.
        this.currentSelectionStart = event.target['selectionStart'];
        this.currentSelectionEnd = event.target['selectionEnd'];

        this.contextMenu.openMenu(source, event);
    }

    /**
     * Ferme le menu contextuel.
     */
    public CloseContextMenu() {
        this.isContextMenuVisible = false;
    }

    /**
     * Levé lorsque l'utilisateur clique sur un élément du menu contextuel.
     * @param event : Événement clic du menu contextuel.
     */
    public handleMenuItemClick(event: ContextMenuItemEvent) {
        let sourceControl: AbstractControl = this.apiConfigurationRequestForm.get(event.source);

        if (sourceControl.value) {
            sourceControl.setValue(sourceControl.value.substring(0, this.currentSelectionStart) + event.data + sourceControl.value.substring(this.currentSelectionEnd));
        } else {
            sourceControl.setValue(event.data);
        }

        this.isContextMenuVisible = false;
    }

    /**
     * Appellé lorsque l'utilisateur clique sur le bouton 'Annuler'.
     */
    public onCancelClick() {
        this.fillForm();
    }

    /**
     * Appellé lorsque l'utilisateur clique sur le bouton 'Enregistrer'.
     */
    onSaveClick() {
        let apiConfiguration = this.apiConfigurationState.$apiConfiguration().createCopy();

        if (!apiConfiguration.requestConfiguration) {
            apiConfiguration.requestConfiguration = new RestApiRequestConfiguration();
        }

        apiConfiguration.requestConfiguration.httpVerb = this.apiConfigurationRequestForm.get('httpVerb').value;

        apiConfiguration.requestConfiguration.url = this.apiConfigurationRequestForm.get('url').value;

        apiConfiguration.requestConfiguration.headers = this.apiConfigurationRequestForm.get('headers').value;
        apiConfiguration.requestConfiguration.authentication.authenticationType = this.apiConfigurationRequestForm.get('authentication').value;
        apiConfiguration.requestConfiguration.authentication.username = this.apiConfigurationRequestForm.get('username').value;
        apiConfiguration.requestConfiguration.authentication.password = this.apiConfigurationRequestForm.get('password').value;
        apiConfiguration.requestConfiguration.authentication.bearer = this.apiConfigurationRequestForm.get('bearer').value;
        apiConfiguration.requestConfiguration.authentication.apiKey = this.apiConfigurationRequestForm.get('apiKey').value;

        if (apiConfiguration.contextOfUse == ContextOfUse.WorkflowAction && apiConfiguration.requestConfiguration.httpVerb !== HttpVerb.Get) {
            apiConfiguration.requestConfiguration.requestFormat = this.apiConfigurationRequestForm.get('requestFormat').value;
            apiConfiguration.requestConfiguration.requestContent = this.apiConfigurationRequestForm.get('requestContent').value;
        } else {
            apiConfiguration.requestConfiguration.requestFormat = null;
            apiConfiguration.requestConfiguration.requestContent = null;
        }

        this.apiConfigurationState.$apiConfiguration.set(apiConfiguration);
    }

    /**
     * Remplit les champs du formulaire avec les données de la configuration d'API.
     *
     */
    private fillForm() {
        if (this.apiConfigurationState.$apiConfiguration().requestConfiguration?.httpVerb) {
            this.apiConfigurationRequestForm.get('httpVerb').setValue(this.apiConfigurationState.$apiConfiguration().requestConfiguration?.httpVerb);
        } else {
            this.apiConfigurationRequestForm.get('httpVerb').setValue(HttpVerb.Get);
        }

        this.apiConfigurationRequestForm.get('url').setValue(this.apiConfigurationState.$apiConfiguration().requestConfiguration?.url);
        this.apiConfigurationRequestForm.get('headers').setValue(this.apiConfigurationState.$apiConfiguration().requestConfiguration?.headers);
        this.apiConfigurationRequestForm.get('authentication').setValue(this.apiConfigurationState.$apiConfiguration().requestConfiguration?.authentication?.authenticationType);
        this.apiConfigurationRequestForm.get('username').setValue(this.apiConfigurationState.$apiConfiguration().requestConfiguration?.authentication?.username);
        this.apiConfigurationRequestForm.get('password').setValue(this.apiConfigurationState.$apiConfiguration().requestConfiguration?.authentication?.password);
        this.apiConfigurationRequestForm.get('bearer').setValue(this.apiConfigurationState.$apiConfiguration().requestConfiguration?.authentication?.bearer);
        this.apiConfigurationRequestForm.get('apiKey').setValue(this.apiConfigurationState.$apiConfiguration().requestConfiguration?.authentication?.apiKey);

        if (this.context.apiConfigurationContext.restApiConfiguration.contextOfUse == ContextOfUse.WorkflowAction) {
            this.apiConfigurationRequestForm.get('requestFormat').setValue(this.apiConfigurationState.$apiConfiguration().requestConfiguration?.requestFormat);
            this.apiConfigurationRequestForm.get('requestContent').setValue(this.apiConfigurationState.$apiConfiguration().requestConfiguration?.requestContent);
        }
    }
}
